<template>
  <div>
    <b-data-table
      :headers="tableHeaders"
      :items="billings"
      style="border: none"
      :page.sync="currentPage"
      @page-sync="(p) => (currentPage = p)"
      disable-sort
      :pagination="billingsPagination"
      :hide-default-footer="!totalItems"
      item-key="billing_id"
      item-value="billing_id"
      :item-expanded.sync="expanded"
      :show-expand="isExpandable"
      single-expand
    >
      <template v-slot:item.total="{ item }">
        {{
          new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
          }).format(item.total)
        }}
      </template>
      <template v-slot:item.period="{ item }">
        {{ getPeriodString(item) }}
      </template>
      <template v-slot:item.pending="{ item }">
        <v-alert :type="getAlertType(item)" class="ma-0" text dense outlined>
          {{ translations[item.payment_status || 'incomplete'][lang] }}
        </v-alert>
      </template>
      <template v-slot:item.payment_at="{ item }">
        <v-btn
          v-if="getPayBtnType(item) == 'stripe'"
          class="text-capitalize"
          depressed
          :disabled="loading"
          :loading="loading"
          color="secondary"
          v-text="translations.payNow[lang]"
          @click="payBilling(item)"
        />
        <span v-else-if="getPayBtnType(item) == 'payment_codes'" class="font-weight-bold">
          {{ item.payment_identifier }}
        </span>
        <span v-else>{{ item.payment_at }}</span>
      </template>
      <template v-slot:item.payment_completed="{ item }">
        <v-alert v-if="item.pending" class="ma-0 text-center" text dense>
          {{ translations.pending[lang] }}
        </v-alert>
        <v-alert v-else :type="getAlertType(item)" class="ma-0" text dense outlined>
          {{ translations[item.payment_status || 'incomplete'][lang] }}
        </v-alert>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-row>
          <v-col cols="12" md="12" class="d-flex">
            <v-tooltip color="secondary" top>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-if="!isExpandable"
                  color="secondary darken-3"
                  icon
                  v-on="on"
                  @click="viewBillingDetails(item)"
                >
                  <v-icon v-text="icons.mdiTableEye"></v-icon>
                </v-btn>
              </template>
              <span class="font-weight-bold" v-text="translations.viewMore[lang]"></span>
            </v-tooltip>
            <v-tooltip v-if="subscription" color="secondary" top>
              <template v-slot:activator="{ on }">
                <v-btn
                  icon
                  color="secondary darken-3"
                  v-on="on"
                  v-if="!item.pending && item.payment_status != 'completed'"
                  @click="openChangePaymentDialog(item)"
                >
                  <v-icon v-text="icons.mdiCreditCardOutline"></v-icon>
                </v-btn>
              </template>
              <span class="font-weight-bold">
                {{ translations.changeToCompleted[lang] }}
              </span>
            </v-tooltip>
            <v-tooltip v-if="shouldShowNotifyButton(item)" color="secondary" top>
              <template v-slot:activator="{ on }">
                <v-btn
                  icon
                  color="secondary darken-3"
                  v-on="on"
                  v-if="!item.payment_completed && !item.pending"
                  @click="openNotifyDialog(item)"
                >
                  <v-icon v-text="icons.mdiMessageTextOutline"></v-icon>
                </v-btn>
              </template>
              <span class="font-weight-bold">
                {{ translations.notifyPayment[lang] }}
              </span>
            </v-tooltip>
            <v-tooltip v-if="subscription" color="secondary" top>
              <template v-slot:activator="{ on }">
                <v-btn
                  icon
                  color="secondary darken-3"
                  v-on="on"
                  v-if="item.invoice_status !== 'PAID' && !item.pending"
                  @click="openInvoiceBillingDialog(item)"
                >
                  <v-icon v-text="icons.mdiReceiptTextSendOutline"></v-icon>
                </v-btn>
              </template>
              <span class="font-weight-bold">
                {{ translations.createInvoiceSendEmail[lang] }}
              </span>
            </v-tooltip>
            <v-tooltip
              v-if="superUser && !subscription && item.payment_status !== 'completed'"
              color="secondary"
              top
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  color="secondary darken-3"
                  icon
                  v-on="on"
                  :loading="loadingBillingId == item.billing_id"
                  @click="openRecalculateModal(item)"
                >
                  <v-icon v-text="icons.mdiReceiptTextEditOutline"></v-icon>
                </v-btn>
              </template>
              <span class="font-weight-bold" v-text="translations.calculateBilling[lang]"></span>
            </v-tooltip>
            <v-tooltip v-if="showCancelBillingBtn(item)" color="secondary" top>
              <template v-slot:activator="{ on }">
                <v-btn
                  color="secondary darken-3"
                  icon
                  v-on="on"
                  :loading="loadingBillingId == item.billing_id"
                  @click="showCancelBillingDialog(item)"
                >
                  <v-icon v-text="icons.mdiReceiptTextRemoveOutline"></v-icon>
                </v-btn>
              </template>
              <span class="font-weight-bold" v-text="translations.cancelBilling[lang]"></span>
            </v-tooltip>
          </v-col>
        </v-row>
      </template>
      <template v-slot:expanded-item="{ item }">
        <div
          class="expanded-row"
          :style="{
            height: 48 * ((item.invoice?.length || 0) + 2) + 12 + 'px',
          }"
        >
          <div class="invoice-container">
            <InvoiceTable
              :ref="`invoiceTable${item.billing_id}`"
              :invoice="item.invoice"
              :billing="item"
            />
          </div>
        </div>
      </template>
    </b-data-table>
    <v-dialog v-model="billingDialogData.show.details" max-width="500">
      <b-card
        :title="translations.billingDetails[lang]"
        @cancel="billingDialogData.show.details = false"
        :submitBtn="false"
      >
        <InvoiceTable
          :invoice="billingDialogData.billing && billingDialogData.billing.invoice"
          :billing="billingDialogData.billing"
        />
      </b-card>
    </v-dialog>

    <v-dialog v-model="billingDialogData.show.cancel" max-width="500">
      <b-card
        v-if="billingDialogData.billing && billingDialogData.billing.billing_id"
        type="delete"
        :title="translations.cancelBilling[lang]"
        :loading="loading"
        :error-message="errorMessage"
        :submit-text="translations.submit"
        @cancel="closeCancelDialog()"
        @submit="cancelingBilling(billingDialogData.billing)"
      >
        <div class="subtitle-1 font-weight-medium pl-2">
          {{ translations.sureCancelBillingOf[lang] }}
          {{ translations.period[lang] }}
          <span class="font-weight-bold text--primary">
            {{ getPeriodString(billingDialogData.billing) }}
          </span>
          {{ translations.and[lang] }}
          {{ translations.total[lang] }}
          <span class="font-weight-bold text--primary">
            €{{ billingDialogData.billing.total || '' }}
          </span>
          {{ translations.questionmark[lang] }}
        </div>
      </b-card>
    </v-dialog>

    <v-dialog v-model="billingDialogData.show.payment" max-width="500">
      <b-card
        v-if="billingDialogData.show.payment"
        type="accept"
        :title="translations.changeToCompleted[lang]"
        :loading="loading"
        :error-message="errorMessage"
        :submit-text="translations.submit"
        @cancel="closeChangePaymentDialog()"
        @submit="changeBillingSettings({ payment: true })"
      >
        <div class="subtitle-1 font-weight-medium pl-2">
          {{ translations.surePayBillingOf[lang] }}
        </div>
        <div class="subtitle-1 text-center font-weight-bold pl-2">
          €{{ billingDialogData.billing.total }}
        </div>
      </b-card>
    </v-dialog>

    <v-dialog v-model="billingDialogData.show.invoice" max-width="500">
      <b-card
        v-if="billingDialogData.show.invoice"
        type="accept"
        :title="translations.invoiceBilling[lang]"
        :loading="loading"
        :error-message="errorMessage"
        :submit-text="translations.submit"
        @cancel="closeInvoiceBillingDialog()"
        @submit="changeBillingSettings({ invoice: true })"
      >
        <div class="subtitle-1 font-weight-medium pl-2">
          {{ translations.sureInvoiceBillingOf[lang] }}
        </div>
        <div class="subtitle-1 text-center font-weight-bold pl-2">
          €{{ billingDialogData.billing.total }}
        </div>
      </b-card>
    </v-dialog>

    <v-dialog v-model="billingDialogData.show.notify" max-width="500">
      <b-card
        type="accept"
        :title="translations.notifyPayment[lang]"
        :loading="loading"
        :error-message="errorMessage"
        :submit-text="translations.submit"
        @cancel="closeNotifyDialog()"
        @submit="notifyPayment"
      >
        <div class="subtitle-1 font-weight-medium pl-2">
          <span class="mb-5 text-subtitle-2"> {{ translations.notifyManager[lang] }}: </span>
          <v-simple-table
            v-if="billingDialogData.show.notify && billingDialogData.billing"
            class="mt-3"
          >
            <template v-slot:default>
              <tbody>
                <tr>
                  <td>{{ translations.period[lang] }}</td>
                  <td>
                    {{ getPeriodString(billingDialogData.billing) }}
                  </td>
                </tr>
                <tr>
                  <td>{{ translations.paymentStatus[lang] }}</td>
                  <td>
                    <v-alert type="warning" class="ma-0" text dense outlined>
                      {{ translations.unpaid[lang] }}
                    </v-alert>
                  </td>
                </tr>
                <tr>
                  <td>{{ translations.total[lang] }}</td>
                  <td>€{{ billingDialogData.billing.total }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
      </b-card>
    </v-dialog>

    <v-dialog v-model="billingItemModal.show" max-width="600">
      <b-card
        :title="translations.billingDetails[lang]"
        @cancel="closeBillingItemModal"
        @submit="exportBillingItemDetails"
        :loading="billingItemModal.loading"
      >
        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th>{{ translations.id[lang] }}</th>
                <th>{{ translations.description[lang] }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="detail in billingItemModal.details" :key="detail.id">
                <td>{{ detail.id }}</td>
                <td>{{ detail.description }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <!-- Export Links -->
        <ExportLinks
          :export-key="`billing-item-${billingItemModal.invoiceItemId}`"
          :query="`?invoice_item_id=${billingItemModal.invoiceItemId}`"
        />
      </b-card>
    </v-dialog>

    <v-dialog v-model="recalculateModal.show" max-width="500">
      <b-card
        v-if="recalculateModal.show && recalculateModal.billing"
        :title="translations.recalculateBilling[lang]"
        :loading="recalculateModal.loading"
        @cancel="closeRecalculateModal"
        @submit="recalculateBilling"
      >
        <div class="subtitle-1 font-weight-medium pl-2">
          {{ translations.sureRecalculateBilling[lang] }}
          <span class="font-weight-bold text--primary">
            {{ getPeriodString(recalculateModal.billing) }}
          </span>
          {{ translations.and[lang] }}
          <span class="font-weight-bold text--primary">
            €{{ recalculateModal.billing.total || '' }}
          </span>
          {{ translations.total[lang].toLowerCase() }}
          {{ translations.questionmark[lang] }}
        </div>
      </b-card>
    </v-dialog>
  </div>
</template>

<script>
import InvoiceTable from '@/components/general/settings/billingDashboard/InvoiceTable.vue';
import translations from '@/utils/translations/general/settings/billingDashboard';
import {
  mdiTableEye,
  mdiReceiptTextEditOutline,
  mdiReceiptTextRemoveOutline,
  mdiCreditCardOutline,
  mdiReceiptTextSendOutline,
  mdiMessageTextOutline,
} from '@mdi/js';
import { mapActions, mapMutations, mapState } from 'vuex';

export default {
  name: 'BillingsTable',
  mixins: [translations],
  components: { InvoiceTable },
  props: {
    billings: Array,
    pagination: Object,
    headers: Array,
    isExpandable: {
      type: Boolean,
      default: false,
    },
    subscription: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      icons: {
        mdiTableEye,
        mdiReceiptTextEditOutline,
        mdiReceiptTextRemoveOutline,
        mdiCreditCardOutline,
        mdiReceiptTextSendOutline,
        mdiMessageTextOutline,
      },
      loadingBillingId: null,
      currentPage: 1,
      billingDialogData: {
        show: {
          details: false,
          payment: false,
          cancel: false,
          notify: false,
          invoice: false,
        },
        billing: {},
      },
      expanded: [],
      billingItemModal: {
        show: false,
        loading: false,
        details: [],
        invoiceItemId: null,
      },
      recalculateModal: {
        show: false,
        loading: false,
        billing: null,
      },
    };
  },
  computed: {
    ...mapState(['loading', 'errorMessage', 'superUser']),
    ...mapState('general/settings/billingDashboard/paymentMethods', ['selectedMethod']),
    lang() {
      return this.$route.params.lang;
    },
    tableHeaders() {
      return this.headers?.length
        ? this.headers
        : [
            {
              text: this.translations.total[this.lang],
              value: 'total',
              align: 'center',
            },
            {
              text: this.translations.paymentStatus[this.lang],
              value: 'pending',
              align: 'center',
            },
            {
              text: this.translations.period[this.lang],
              value: 'period',
              align: 'center',
            },
            {
              text: this.translations.payment_date[this.lang],
              value: 'payment_at',
              align: 'center',
            },
            {
              text: this.translations.actions[this.lang],
              value: 'actions',
              align: 'center',
            },
          ];
    },
    billingsPagination() {
      return this.pagination || null;
    },
    page() {
      return this.pagination?.current_page || 1;
    },
    totalItems() {
      return this.pagination?.total || null;
    },
    paymentMethod() {
      return this.subscription?.store_payment_method?.system_payment_method_category?.system || '';
    },
  },
  methods: {
    ...mapMutations(['setDeleteDialog', 'setNotification', 'setLoading']),
    ...mapActions('general/settings/billingDashboard/paymentHistory', ['billingPayment']),
    ...mapActions('general/settings/billingDashboard', [
      'fetchBillingDashboardData',
      'calculateBilling',
    ]),
    ...mapActions('loyaltyPanel/subscriptions/subscriptionBillings', [
      'updateStatus',
      'notifyBilling',
      'cancelBilling',
      'getBillings',
    ]),

    viewBillingDetails(item) {
      this.billingDialogData.show.details = true;
      this.billingDialogData.billing = item;
    },

    async startCalculateBilling(id) {
      try {
        this.loadingBillingId = id;
        await this.calculateBilling(id);
        this.loadingBillingId = null;
        this.setNotification({
          show: true,
          type: 'success',
          text: this.translations.billingUpdated[this.lang],
        });
      } catch (error) {
        this.loadingBillingId = null;
        console.log('error:', error.data);
        this.setNotification({
          show: true,
          type: 'error',
          text: error.response?.data?.message || 'Error calculating billing',
        });
      }
    },
    async payBilling(billing) {
      const data = await this.billingPayment(billing.billing_id);
      if (data.code === 200) {
        billing.payment_completed = data.data.payment_completed;
        billing.payment_at = data.data.payment_at;
        this.fetchBillingDashboardData();
      }
    },
    getPayBtnType(item) {
      if (
        !this.subscription &&
        ['incomplete', 'failed'].includes(item.payment_status || 'incomplete')
      ) {
        return this.selectedMethod?.system_payment_category?.system || '';
      }
      return null;
    },
    showCancelBillingBtn(item) {
      return (
        this.superUser &&
        this.subscription &&
        !item.pending &&
        ['incomplete', 'failed'].includes(item.payment_status || 'incomplete')
      );
    },
    showCancelBillingDialog(billing) {
      this.billingDialogData.show.cancel = true;
      this.billingDialogData.billing = billing;
    },
    closeCancelDialog() {
      this.billingDialogData.show.cancel = false;
      this.billingDialogData.billing = {};
    },
    getAlertType(item) {
      return !['completed', 'failed', 'cancelled'].includes(item.payment_status)
        ? 'warning'
        : item.payment_status === 'completed'
          ? 'success'
          : 'error';
    },
    getPeriodString(item) {
      if (item.periodVal) {
        return item.periodVal;
      }
      let period = item.period || item.periodVal;
      if (!period) return '-';
      let start = new Date(period.from);
      let end = new Date(period.to);
      return `${start.toLocaleDateString(this.lang)} - ${end.toLocaleDateString(this.lang)}`;
    },
    openChangePaymentDialog(item) {
      this.billingDialogData.billing = item;
      this.billingDialogData.show.payment = true;
    },
    openInvoiceBillingDialog(item) {
      this.billingDialogData.billing = item;
      this.billingDialogData.show.invoice = true;
    },
    closeChangePaymentDialog() {
      this.billingDialogData.billing = null;
      this.billingDialogData.show.payment = false;
    },
    closeInvoiceBillingDialog() {
      this.billingDialogData.billing = null;
      this.billingDialogData.show.invoice = false;
    },
    openNotifyDialog(item) {
      this.billingDialogData.billing = item;
      this.billingDialogData.show.notify = true;
    },
    closeNotifyDialog() {
      this.billingDialogData.billing = null;
      this.billingDialogData.show.notify = false;
    },
    async notifyPayment() {
      if (this.billingDialogData.billing?.billing_id) {
        let res = await this.notifyBilling(this.billingDialogData.billing);
        if (!res.error) {
          this.setNotification({
            show: true,
            type: 'success',
            text: this.translations.notificationSent[this.lang],
          });
        }
      }
    },
    shouldShowNotifyButton(item) {
      if (this.subscription && this.paymentMethod === 'payment_codes') {
        return ['incomplete', 'failed'].includes(item.payment_status || 'incomplete');
      }
      return false;
    },

    setBillingData(billing, data) {
      billing.payment_completed = data.payment_completed;
      billing.payment_status = data.payment_status;
      billing.pending = data.pending;
      billing.payment_at = data.payment_at;
      billing.invoice_status = data.invoice_status;
    },

    async changeBillingSettings(body) {
      let billing = this.billingDialogData.billing;
      let res = await this.updateStatus({
        id: billing.billing_id,
        body,
      });
      this.setBillingData(billing, res.data);
      if (res.error) {
        this.setNotification({
          show: true,
          type: 'error',
          text: res.message,
        });
      } else {
        this.closeChangePaymentDialog();
        this.closeInvoiceBillingDialog();
        this.getBillings({
          id: this.subscription.store_subscription_id,
          page: this.page,
        });
      }
    },

    async cancelingBilling() {
      let billing = this.billingDialogData.billing;
      let resData = await this.cancelBilling(billing);
      if (!resData.error) {
        this.setBillingData(billing, resData);
        this.getBillings({
          id: this.subscription.store_subscription_id,
          page: this.page,
        });

        this.setNotification({
          show: true,
          type: 'success',
          text: this.translations.successCancelBilling[this.lang],
        });
      } else {
        this.setNotification({
          show: true,
          type: 'error',
          text: resData.message || 'Error cancelling billing',
        });
      }
      this.closeCancelDialog();
    },
    async openBillingItemModal(item) {
      this.billingItemModal.invoiceItemId = item.invoice_item_id;
      this.billingItemModal.loading = true;

      const response = await this.$store.dispatch(
        'general/settings/billingDashboard/getInvoiceItemUsages',
        { invoice_item_id: item.invoice_item_id }
      );

      this.billingItemModal.details = response.data.results;
      this.billingItemModal.loading = false;
      this.billingItemModal.show = true;
    },

    closeBillingItemModal() {
      this.billingItemModal.show = false;
      this.billingItemModal.details = [];
      this.billingItemModal.invoiceItemId = null;
    },

    async exportBillingItemDetails() {
      this.billingItemModal.loading = true;

      const response = await this.$store.dispatch(
        'general/settings/billingDashboard/exportInvoiceItemDetails',
        { invoice_item_id: this.billingItemModal.invoiceItemId }
      );

      this.billingItemModal.loading = false;

      if (response.error) {
        this.$store.commit('setNotification', {
          show: true,
          type: 'error',
          text: response.message,
        });
      } else {
        this.$store.commit('setNotification', {
          show: true,
          type: 'success',
          text: translations.exportSuccess[this.$route.params.lang],
        });
      }
    },
    openRecalculateModal(billing) {
      this.recalculateModal.billing = billing;
      this.recalculateModal.show = true;
    },

    closeRecalculateModal() {
      this.recalculateModal.show = false;
      this.recalculateModal.billing = null;
    },

    async recalculateBilling() {
      try {
        this.recalculateModal.loading = true;
        const res = await this.calculateBilling(this.recalculateModal.billing.billing_id);
        console.log(res, res.error);
        if (res.error) {
          throw new Error(res.message || 'Error recalculating billing');
        }
        this.recalculateModal.loading = false;
        this.closeRecalculateModal();
        this.setNotification({
          show: true,
          type: 'success',
          text: this.translations.billingUpdated[this.lang],
        });
      } catch (error) {
        this.recalculateModal.loading = false;
        console.error('Error recalculating billing:', error);
        this.setNotification({
          show: true,
          type: 'error',
          text: error.response?.data?.message || error.message || 'Error recalculating billing',
        });
      }
    },
  },

  mounted() {
    this.currentPage = this.pagination?.current_page || 1;
  },

  watch: {
    'billingDialogData.billing': {
      immediate: true,
      handler(val) {
        if (val && typeof val.invoice == 'object' && val.invoice?.length === 0) {
          val.invoice = null;
        }
      },
    },
    currentPage: {
      immediate: true,
      handler(val) {
        if (this.pagination) {
          this.pagination.current_page = val || 1;
        }
      },
    },
  },
};
</script>

<style>
.expanded-row {
  width: 100%;
  padding: 0;
  transition: height 0.6s ease;
}

.invoice-container {
  position: absolute;
  height: 100%;
  width: 100%;
}

.invoice-container > div {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
}
</style>
